/* Syncs CSS --primary, --chart-*, and related variables to the active kiwi
   skin's palette when the "kiwi" color theme is selected. */

import { useLayoutEffect } from "react";
import { PALETTES } from "@/components/brand/kiwiPalettes";
import type { KiwiSkinConfig } from "@/types/kiwiSkin";

/** Hex → approximate relative luminance (0–1). */
function hexToRelativeLuminance(hex: string): number {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Parse hex to [r, g, b] 0-255. */
function hexToRgb(hex: string): [number, number, number] {
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}

/** Mix two colors by ratio (0 = a, 1 = b). */
function mixHex(a: string, b: string, ratio: number): string {
  const [ar, ag, ab] = hexToRgb(a);
  const [br, bg, bb] = hexToRgb(b);
  const r = Math.round(ar + (br - ar) * ratio);
  const g = Math.round(ag + (bg - ag) * ratio);
  const bl = Math.round(ab + (bb - ab) * ratio);
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${bl.toString(16).padStart(2, "0")}`;
}

const KIWI_CSS_VARS = [
  "--primary",
  "--primary-foreground",
  "--ring",
  "--sidebar-primary",
  "--sidebar-primary-foreground",
  "--ai-accent",
  "--chart-1",
  "--chart-2",
  "--chart-3",
  "--chart-4",
  "--chart-5",
] as const;

export function useKiwiThemeSync(
  colorTheme: string,
  activeSkin: KiwiSkinConfig | null | undefined,
) {
  useLayoutEffect(() => {
    const root = document.documentElement;

    if (colorTheme !== "kiwi" || !activeSkin) {
      for (const v of KIWI_CSS_VARS) {
        root.style.removeProperty(v);
      }
      return;
    }

    const palette = PALETTES[activeSkin.palette];
    const bodyColor = palette.body;
    const accentColor = palette.accent;
    const highlightColor = palette.highlight;
    const lum = hexToRelativeLuminance(bodyColor);
    const fg = lum > 0.5 ? "#1a1a1a" : "#ffffff";

    root.style.setProperty("--primary", bodyColor);
    root.style.setProperty("--primary-foreground", fg);
    root.style.setProperty("--ring", bodyColor);
    root.style.setProperty("--sidebar-primary", bodyColor);
    root.style.setProperty("--sidebar-primary-foreground", fg);
    root.style.setProperty("--ai-accent", accentColor);

    // Chart colors derived from the skin palette
    root.style.setProperty("--chart-1", bodyColor);
    root.style.setProperty("--chart-2", accentColor);
    root.style.setProperty("--chart-3", highlightColor);
    root.style.setProperty("--chart-4", mixHex(bodyColor, accentColor, 0.5));
    root.style.setProperty("--chart-5", mixHex(bodyColor, "#000000", 0.3));

    return () => {
      for (const v of KIWI_CSS_VARS) {
        root.style.removeProperty(v);
      }
    };
  }, [colorTheme, activeSkin]);
}
